import { useContext } from "react";
import { AppContext } from "../App";
import Allergens from "./Allergens";
import NumberFormat from "react-number-format";
import Label from "./Label";
import CloseIcon from "../icons/CloseIcon";
import Tags from "./Tags";
import ProductLabels from "./ProductLabels";

// Riga di un sottoprodotto: nome, icone allergeni e, se abilitata, descrizione completa
function SubproductLine({ subproduct, hasPrice }) {
	const { getTranslatedString, shop, settings } = useContext(AppContext);
	const showDescription = settings?.menu_api?.show_subproducts_description == 1;
	const description = getTranslatedString(subproduct, "description");
	const allergens = subproduct.allergens || [];

	return (
		<div className="price-line subproduct-line">
			<div className="subproduct-main">
				<div className="subproduct-name">
					{getTranslatedString(subproduct, "name")}
					{allergens.length > 0 && (
						<span className="subproduct-allergens allergens">
							<Allergens
								allergens={allergens}
								color="#000"
								backgroundColor="#fff"
								iconsOnly
							/>
						</span>
					)}
				</div>
				{showDescription && description && (
					<div
						className="subproduct-description"
						dangerouslySetInnerHTML={{ __html: description }}
					/>
				)}
			</div>
			{hasPrice && (
				<NumberFormat
					value={subproduct.price}
					displayType={"text"}
					thousandSeparator="."
					decimalSeparator=","
					suffix={shop.currency == "CHF" ? " CHF" : " €"}
					isNumericString={true}
				/>
			)}
		</div>
	);
}

function Product() {
	const { product, setProduct, getTranslatedString, shop } = useContext(AppContext);

	const hasPricedMandatoryVariants = (product) => {
		return product.variants_categories.find((vc) => vc.has_price == 1 && vc.min > 0);
	};

	const getVariantCategoryLimitsText = (variantsCategory) => {
		const min = Number(variantsCategory.min);
		const max = Number(variantsCategory.max);
		const minText = Number.isFinite(min) ? min : 0;
		const maxText = Number.isFinite(max) ? max : 0;

		return `Min: ${minText} - Max: ${maxText}`;
	};

	const renderVariantCategoryLimits = (variantsCategory) => {
		const limitsText = getVariantCategoryLimitsText(variantsCategory);
		if (!limitsText) return null;

		return <span className="variant-category-limits">{limitsText}</span>;
	};

	return (
		<div className="sheet">
			<div className="content">
				<div className="scroll-content">
					<div className="sheet-hero">
						<div className="buttons-groups right">
							<button onClick={() => setProduct(false)}>
								<CloseIcon></CloseIcon>
							</button>
						</div>
						{product.img && (
							<div className="img">
								<img src={product.img} alt={product.name} />
							</div>
						)}
					</div>
					<Tags product={product} />
					<ProductLabels product={product} />
					<div className="title">{getTranslatedString(product, "name")}</div>
					<div
						className="description"
						dangerouslySetInnerHTML={{
							__html: getTranslatedString(product, "description"),
						}}
					/>
					<div className="price">
						{hasPricedMandatoryVariants(product) ? (
							<span className="info">
								<Label number={13} />
							</span>
						) : (
							""
						)}
						<NumberFormat
							value={product.price}
							displayType={"text"}
							thousandSeparator="."
							decimalSeparator=","
							suffix={shop.currency == "CHF" ? " CHF" : " €"}
							isNumericString={true}
						/>
					</div>
					<div className="container-fluid">
						<div className="row">
							{product.allergens.length > 0 && (
								<div className="col-sm-6">
									<div className="info-box">
										<h5 className="subtitle">
											<Label number={8} />
										</h5>
										<div className="info-box-content allergens">
											<Allergens
												allergens={product.allergens}
												color="#000"
												backgroundColor="#fff"
											/>
										</div>
									</div>
								</div>
							)}
							{product.ingredients.length > 0 && (
								<div className="col-sm-6">
									<div className="info-box">
										<h5 className="subtitle">
											<Label number={9} />
										</h5>
										<div className="info-box-content ingredients">
											{product.ingredients.map((ingredient, i) => (
												<div key={i} className="ingredient-item">
													{getTranslatedString(ingredient, "name")}
												</div>
											))}
										</div>
									</div>
								</div>
							)}
						</div>
						{product.variants_categories.length > 0 && (
							<div className="info-box">
								<h5 className="subtitle">
									<Label number={10} />
								</h5>
								<div className="info-box-content">
									{product.variants_categories.map((variants_category, i) => (
										<div key={i} className="info-box-category-variant">
											<div className="subtitle info-box-category-variant-title">
												{getTranslatedString(variants_category, "name")}
												{renderVariantCategoryLimits(variants_category)}
											</div>
											{variants_category.variants.map((variant, n) => (
												<div key={n} className="price-line">
													<div>
														{getTranslatedString(variant, "name")}
													</div>
													{variants_category.has_price == 1 && (
														<NumberFormat
															value={variant.price}
															displayType={"text"}
															thousandSeparator="."
															decimalSeparator=","
															suffix={
																shop.currency == "CHF"
																	? " CHF"
																	: " €"
															}
															isNumericString={true}
														/>
													)}
												</div>
											))}
											{variants_category.variants2.map((variant, n) => (
												<div key={n} className="price-line">
													<div>
														{getTranslatedString(variant, "name")}
													</div>
													{variants_category.has_price == 1 && (
														<NumberFormat
															value={variant.price}
															displayType={"text"}
															thousandSeparator="."
															decimalSeparator=","
															suffix={
																shop.currency == "CHF"
																	? " CHF"
																	: " €"
															}
															isNumericString={true}
														/>
													)}
												</div>
											))}
											{variants_category.products.map((subproduct, n) => (
												<SubproductLine
													key={n}
													subproduct={subproduct}
													hasPrice={variants_category.has_price == 1}
												/>
											))}
											{variants_category.products2.map((subproduct, n) => (
												<SubproductLine
													key={n}
													subproduct={subproduct}
													hasPrice={variants_category.has_price == 1}
												/>
											))}
										</div>
									))}
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}

export default Product;
